import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Shield, User, Lock, X } from 'lucide-react'
import { authApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'

const AdminLogin = () => {
  const navigate = useNavigate()
  const { login, user } = useAuth()
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    document.title = '管理员登录 - 眉山疾控积分系统'
    if (user?.role === 'admin') navigate('/admin', { replace: true })
    if (user?.role === 'medical' && user?.medicalRole === 'supervisor') navigate('/admin', { replace: true })
  }, [user, navigate])

  const handleLogin = async () => {
    setError('')
    if (!phone || !password) {
      setError('请输入手机号和密码')
      return
    }
    setLoading(true)
    try {
      const resp = await authApi.login(phone, password)
      const role = resp.account?.role
      const medical_role = (resp.account as any)?.medical_role
      // 允许 admin 或 supervisor（medical_role=supervisor）登录
      const isAdmin = role === 'admin' || medical_role === 'supervisor'
      if (!isAdmin) {
        setError('该账号没有管理员或机构负责人权限')
        return
      }
      login(resp)
      navigate('/admin', { replace: true })
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '登录失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin()
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-700 to-green-600 flex">
      {/* 左侧动态图标区域 */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
        <div className="text-center">
          <svg className="w-64 h-64 mx-auto mb-8" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.2)" strokeWidth="4" fill="none" />
            <circle cx="100" cy="100" r="70" stroke="rgba(255,255,255,0.15)" strokeWidth="3" fill="none" className="animate-pulse" />
            <circle cx="100" cy="100" r="50" stroke="rgba(255,255,255,0.1)" strokeWidth="2" fill="none" className="animate-pulse" />
            <path d="M100 30 L150 50 L150 90 C150 130 100 160 100 160 C100 160 50 130 50 90 L50 50 Z" fill="rgba(255,255,255,0.9)" />
            <rect x="90" y="60" width="20" height="50" rx="2" fill="#2B5D3A" />
            <rect x="80" y="80" width="40" height="20" rx="2" fill="#2B5D3A" />
            <path d="M100 180 C90 170 70 170 70 155 C70 140 90 130 100 145 C110 130 130 140 130 155 C130 170 110 170 100 180Z" fill="rgba(255,255,255,0.3)" />
            <circle cx="100" cy="20" r="6" fill="white" className="animate-ping" />
            <circle cx="165" cy="60" r="4" fill="white" className="animate-ping" style={{ animationDelay: '0.5s' }} />
            <circle cx="165" cy="130" r="5" fill="white" className="animate-ping" style={{ animationDelay: '1s' }} />
            <circle cx="35" cy="60" r="4" fill="white" className="animate-ping" style={{ animationDelay: '0.3s' }} />
            <circle cx="35" cy="130" r="5" fill="white" className="animate-ping" style={{ animationDelay: '0.8s' }} />
          </svg>
          <h2 className="text-3xl font-bold text-white mb-4">眉山疾控积分管理系统</h2>
          <p className="text-green-200 text-lg">安全管理 · 高效便捷 · 智能统计</p>
        </div>
      </div>

      {/* 右侧登录表单 */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center mb-8 lg:justify-start">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-7 h-7 text-green-600" />
            </div>
            <span className="ml-3 text-2xl font-bold text-white">管理员登录</span>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center lg:text-left">欢迎回来</h3>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center">
                <X className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                <span className="text-red-600 text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-5" onKeyDown={handleKeyDown}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">手机号</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    placeholder="请输入手机号"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请输入密码"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleLogin}
                disabled={loading}
                className={`w-full py-3 rounded-xl font-medium transition-all ${
                  loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-700 text-white hover:bg-green-800 active:scale-[0.98]'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    登录中...
                  </span>
                ) : (
                  '登录'
                )}
              </button>
            </div>
          </div>

          <p className="text-center text-white/50 text-sm mt-6">© 2026 眉山疾控</p>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
